import { useCallback, useRef } from 'react';

/**
 * Long-press that can't fire the thing under your thumb.
 *
 * Three guards, each for a real way this goes wrong on a phone:
 *  - The press is cancelled if the finger MOVES more than a few pixels, so
 *    scrolling a message list never opens a menu.
 *  - After firing, the following click/tap is swallowed. Without that, lifting
 *    your finger lands a tap on whatever the menu just rendered under it — which
 *    is exactly how someone blocks a person by accident.
 *  - The context menu is suppressed, so iOS doesn't show its own copy/share
 *    sheet on top of ours.
 */
export default function useLongPress(onLongPress, { delay = 500, moveTolerance = 8 } = {}) {
  const timer = useRef(null);
  const start = useRef(null);
  const fired = useRef(false);

  const clear = useCallback(() => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
  }, []);

  const begin = useCallback((e) => {
    const point = e.touches?.[0] || e;
    start.current = { x: point.clientX, y: point.clientY };
    fired.current = false;
    clear();
    timer.current = setTimeout(() => {
      fired.current = true;
      onLongPress?.(e);
    }, delay);
  }, [onLongPress, delay, clear]);

  const move = useCallback((e) => {
    if (!timer.current || !start.current) return;
    const point = e.touches?.[0] || e;
    const dx = Math.abs(point.clientX - start.current.x);
    const dy = Math.abs(point.clientY - start.current.y);
    if (dx > moveTolerance || dy > moveTolerance) clear();
  }, [clear, moveTolerance]);

  const end = useCallback(() => { clear(); }, [clear]);

  // Runs in the capture phase so it beats any handler on the element itself.
  const onClickCapture = useCallback((e) => {
    if (!fired.current) return;
    fired.current = false;
    e.preventDefault();
    e.stopPropagation();
  }, []);

  return {
    onTouchStart: begin,
    onTouchMove: move,
    onTouchEnd: end,
    onTouchCancel: end,
    onMouseDown: begin,
    onMouseMove: move,
    onMouseUp: end,
    onMouseLeave: end,
    onClickCapture,
    onContextMenu: (e) => e.preventDefault(),
  };
}
